/**
 * TradingChartLoader — premium fintech loader.
 * A circular pulse/spinner transforms into a rising trading chart line.
 * Pure SVG + CSS; no heavy libs; respects prefers-reduced-motion.
 *
 * Props:
 *   size: "sm" | "md" | "lg" | "xl"  (default "md")
 *   text: optional string shown below the loader
 *   fullScreen: if true, center in full viewport
 *   className: extra wrapper class
 */
export function TradingChartLoader({
  size = "md",
  text,
  fullScreen = false,
  className = "",
  testId = "trading-loader",
}) {
  const sizes = {
    sm: { box: "h-8 w-8", text: "text-[11px]" },
    md: { box: "h-14 w-14", text: "text-xs" },
    lg: { box: "h-20 w-20", text: "text-sm" },
    xl: { box: "h-28 w-28", text: "text-base" },
  };
  const s = sizes[size] || sizes.md;

  const inner = (
    <div
      role="status"
      aria-label={text || "Loading content"}
      aria-live="polite"
      className={`inline-flex flex-col items-center justify-center gap-3 ${className}`}
      data-testid={testId}
    >
      <div className={`relative ${s.box}`}>
        {/* Outer soft pulse ring */}
        <span className="tcl-pulse absolute inset-0 rounded-full bg-primary/8" />
        {/* Rotating gradient ring */}
        <svg className="tcl-spin absolute inset-0 h-full w-full" viewBox="0 0 48 48" aria-hidden="true">
          <defs>
            <linearGradient id="tcl-ring" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
            </linearGradient>
          </defs>
          <circle
            cx="24"
            cy="24"
            r="21"
            fill="none"
            stroke="url(#tcl-ring)"
            strokeWidth="1.75"
            strokeLinecap="round"
            className="text-primary"
            pathLength="100"
            strokeDasharray="70 30"
          />
        </svg>
        {/* Chart line + candlestick hints */}
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 48 48" aria-hidden="true">
          {/* faint candlestick bars */}
          <g className="tcl-candles" opacity="0.35">
            <line x1="12" y1="34" x2="12" y2="30" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" className="text-primary" />
            <line x1="18" y1="32" x2="18" y2="26" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" className="text-primary" />
            <line x1="24" y1="30" x2="24" y2="24" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" className="text-primary" />
            <line x1="30" y1="26" x2="30" y2="20" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" className="text-primary" />
            <line x1="36" y1="22" x2="36" y2="16" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" className="text-primary" />
          </g>
          {/* rising chart line */}
          <path
            d="M10 34 L16 30 L22 28 L28 22 L34 19 L40 13"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="tcl-line text-primary"
          />
          {/* endpoint dot with glow */}
          <g className="tcl-dot">
            <circle cx="40" cy="13" r="3" fill="currentColor" opacity="0.25" className="text-primary" />
            <circle cx="40" cy="13" r="1.75" fill="currentColor" className="text-primary" />
          </g>
        </svg>
      </div>
      {text && <p className={`${s.text} text-muted-foreground tracking-wide`}>{text}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        {inner}
      </div>
    );
  }
  return inner;
}
